/**
 * Design-system primitives.
 *
 * Every interactive element here is a real semantic element with `aria-*` wiring and a visible
 * focus ring — there are no `div`s pretending to be buttons. Components are grouped in one module
 * because they are small and always imported together; larger ones live in their own files.
 */
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export { Button, ButtonLink } from './Button';
export type { ButtonProps, ButtonSize, ButtonVariant } from './Button';

/* ------------------------------------------------------------------ layout & typography */

export function Section({
  children,
  className,
  background = 'light',
  id,
  as: Tag = 'section',
  'aria-labelledby': labelledBy,
}: {
  children: ReactNode;
  className?: string;
  background?: 'light' | 'subtle' | 'dark' | 'mesh';
  id?: string;
  as?: 'section' | 'div';
  'aria-labelledby'?: string;
}) {
  const backgrounds = {
    light: 'bg-surface',
    subtle: 'bg-raised',
    dark: 'band-dark',
    mesh: 'band-mesh',
  } as const;

  return (
    <Tag id={id} aria-labelledby={labelledBy} className={cn('section', backgrounds[background], className)}>
      <div className="shell">{children}</div>
    </Tag>
  );
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-eyebrow uppercase text-primary', className)}>{children}</p>;
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'left',
  level = 2,
  id,
  action,
  className,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
  level?: 2 | 3;
  id?: string;
  action?: ReactNode;
  className?: string;
}) {
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        align === 'center' && 'items-center text-center',
        action && 'md:flex-row md:items-end md:justify-between',
        className,
      )}
    >
      <div className={cn('max-w-2xl', align === 'center' && 'mx-auto')}>
        {eyebrow && <Eyebrow className="mb-3">{eyebrow}</Eyebrow>}
        <Heading id={id} className={level === 2 ? 'text-display-2' : 'text-display-3'}>
          {title}
        </Heading>
        {description && <p className="mt-4 text-lead text-muted">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Prose({ html, className }: { html: string; className?: string }) {
  // Content is sanitised server-side on write (see apps/core/sanitize.py), so the stored HTML is
  // already safe; rendering it here is the point of a rich-text field.
  return <div className={cn('prose-content', className)} dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ------------------------------------------------------------------ surfaces */

export function Card({
  children,
  className,
  interactive,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
  as?: 'div' | 'article' | 'li';
}) {
  return (
    <Tag
      className={cn(
        'rounded-lg border border-line bg-surface p-6',
        interactive &&
          'transition-[transform,box-shadow,border-color] duration-standard ease-emphasis hover:-translate-y-0.5 hover:border-line-strong hover:shadow-raised focus-within:border-primary',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * A card whose whole area is clickable. The anchor covers the card via a pseudo-element so the
 * accessible name stays on the heading link rather than swallowing the entire card's text.
 */
export function LinkCard({
  to,
  children,
  className,
  label,
}: {
  to: string;
  children: ReactNode;
  className?: string;
  label: string;
}) {
  return (
    <div className={cn('group relative isolate', className)}>
      {children}
      <Link
        to={to}
        aria-label={label}
        className="absolute inset-0 z-10 rounded-lg focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <span className="sr-only">{label}</span>
      </Link>
    </div>
  );
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'primary' | 'accent' | 'signal' | 'success' | 'warning' | 'danger';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-sunken text-ink',
    primary: 'bg-primary-soft text-primary',
    accent: 'bg-accent-soft text-accent',
    signal: 'bg-signal-soft text-signal',
    success: 'bg-accent-soft text-success',
    warning: 'bg-signal-soft text-warning',
    danger: 'bg-danger-soft text-danger',
  } as const;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold leading-none',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Chip({
  children,
  active,
  onClick,
  count,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  count?: number;
}) {
  if (!onClick) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-sm text-ink-soft">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors duration-micro',
        active
          ? 'border-primary bg-primary text-primary-contrast'
          : 'border-line bg-surface text-ink-soft hover:border-primary hover:text-primary',
      )}
    >
      {children}
      {typeof count === 'number' && <span className={cn('text-xs', active ? 'opacity-80' : 'text-muted')}>{count}</span>}
    </button>
  );
}

export function Stat({
  value,
  label,
  prefix,
  suffix,
  description,
  inverse,
}: {
  value: string;
  label: string;
  prefix?: string;
  suffix?: string;
  description?: string;
  inverse?: boolean;
}) {
  return (
    <div>
      <p className={cn('font-display text-4xl font-bold tracking-tight', inverse ? 'text-white' : 'text-ink')}>
        {prefix}
        {value}
        {suffix && <span className="text-accent">{suffix}</span>}
      </p>
      <p className={cn('mt-2 text-sm font-semibold', inverse ? 'text-white/90' : 'text-ink')}>{label}</p>
      {description && <p className={cn('mt-1 text-sm', inverse ? 'text-white/65' : 'text-muted')}>{description}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ feedback */

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const tones = {
    info: 'border-primary/30 bg-primary-soft text-ink',
    success: 'border-success/30 bg-accent-soft text-ink',
    warning: 'border-warning/30 bg-signal-soft text-ink',
    danger: 'border-danger/30 bg-danger-soft text-ink',
  } as const;

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('rounded-md border px-4 py-3 text-sm', tones[tone], className)}
    >
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={cn(title && 'mt-1', 'text-ink-soft')}>{children}</div>}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-muted">
      <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('relative block overflow-hidden rounded-md bg-sunken', className)}
    >
      <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/45 to-transparent" />
    </span>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed border-line-strong bg-raised px-6 py-14 text-center">
      {icon && <div className="mx-auto mb-4 text-muted">{icon}</div>}
      <p className="font-display text-lg font-semibold text-ink">{title}</p>
      {description && <p className="mx-auto mt-2 max-w-md text-sm text-muted">{description}</p>}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ forms */

export function FormField({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="ml-1 text-danger" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {hint && (
        <p id={`${htmlFor}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {children}
      {/* Errors are announced: assistive tech should hear a validation failure, not just see it. */}
      {error && (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const fieldStyles =
  'w-full rounded-md border border-line-strong bg-surface px-3.5 py-2.5 text-[0.9375rem] text-ink ' +
  'placeholder:text-muted/70 transition-[border-color,box-shadow] duration-micro ' +
  'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:bg-sunken disabled:opacity-70 ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/25';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { error?: boolean }>(
  function Input({ className, error, ...rest }, ref) {
    return <input ref={ref} aria-invalid={error || undefined} className={cn(fieldStyles, className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean }>(
  function Textarea({ className, error, rows = 5, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={error || undefined}
        className={cn(fieldStyles, 'resize-y', className)}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { error?: boolean; children: ReactNode }
>(function Select({ className, error, children, ...rest }, ref) {
  return (
    <select ref={ref} aria-invalid={error || undefined} className={cn(fieldStyles, 'pr-9', className)} {...rest}>
      {children}
    </select>
  );
});

export function Checkbox({
  label,
  id,
  error,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; id: string; error?: boolean }) {
  return (
    <div className="flex items-start gap-2.5">
      <input
        id={id}
        type="checkbox"
        aria-invalid={error || undefined}
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0 rounded border-line-strong text-primary',
          'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          error && 'border-danger',
        )}
        {...rest}
      />
      <label htmlFor={id} className="text-sm text-ink-soft">
        {label}
      </label>
    </div>
  );
}

/**
 * File input with client-side extension and size checks. Validation is duplicated on the server
 * (extension, MIME type and byte signature) — this copy exists only to give immediate feedback.
 */
export function FileUpload({
  id,
  accept,
  maxSizeBytes,
  onSelect,
  error,
  hint,
  required,
}: {
  id: string;
  accept: string;
  maxSizeBytes: number;
  onSelect: (file: File | null, error?: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
}) {
  const [fileName, setFileName] = useState('');
  const allowed = accept.split(',').map((value) => value.trim().toLowerCase());

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      setFileName('');
      onSelect(null);
      return;
    }
    const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (allowed.length && !allowed.includes(extension)) {
      setFileName('');
      onSelect(null, `${extension} files are not accepted. Allowed: ${allowed.join(', ')}.`);
      return;
    }
    if (file.size > maxSizeBytes) {
      setFileName('');
      onSelect(null, `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${Math.round(maxSizeBytes / 1024 / 1024)} MB.`);
      return;
    }
    setFileName(file.name);
    onSelect(file);
  }

  return (
    <div className="flex flex-col gap-2">
      <input
        id={id}
        type="file"
        accept={accept}
        required={required}
        onChange={handleChange}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className={cn(
          'w-full cursor-pointer rounded-md border border-dashed border-line-strong bg-raised px-3.5 py-3 text-sm text-ink-soft',
          'file:mr-3 file:rounded file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary-contrast',
          'hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          error && 'border-danger',
        )}
      />
      {hint && (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {fileName && <p className="text-xs font-medium text-success">Selected: {fileName}</p>}
      {error && (
        <p role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ disclosure */

export function Accordion({
  items,
  className,
  defaultOpenIndex,
}: {
  items: { id?: string; question: string; answer: ReactNode }[];
  className?: string;
  defaultOpenIndex?: number;
}) {
  // Native <details> gives keyboard support, in-page find and no-JS behaviour for free.
  return (
    <div className={cn('divide-y divide-line rounded-lg border border-line', className)}>
      {items.map((item, index) => (
        <details
          key={item.id ?? item.question}
          open={index === defaultOpenIndex}
          className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden"
        >
          <summary className="flex cursor-pointer items-start justify-between gap-4 font-medium text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <span>{item.question}</span>
            <span aria-hidden="true" className="mt-1 shrink-0 text-primary transition-transform group-open:rotate-45">
              +
            </span>
          </summary>
          <div className="mt-3 text-[0.9375rem] leading-relaxed text-muted">{item.answer}</div>
        </details>
      ))}
    </div>
  );
}

interface TabsContextValue {
  active: string;
  setActive: (value: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

export function Tabs({
  items,
  children,
  className,
  label,
}: {
  items: { value: string; label: string }[];
  children: ReactNode;
  className?: string;
  label: string;
}) {
  const baseId = useId();
  const [active, setActive] = useState(items[0]?.value ?? '');
  const listRef = useRef<HTMLDivElement>(null);

  // Roving arrow-key navigation, per the WAI-ARIA tabs pattern.
  function handleKeyDown(event: React.KeyboardEvent) {
    const currentIndex = items.findIndex((item) => item.value === active);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % items.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + items.length) % items.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = items.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = items[nextIndex];
    if (!next) return;
    setActive(next.value);
    listRef.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(`${baseId}-tab-${next.value}`)}`)?.focus();
  }

  return (
    <TabsContext.Provider value={{ active, setActive, baseId }}>
      <div className={className}>
        <div ref={listRef} role="tablist" aria-label={label} className="scrollbar-none flex gap-1 overflow-x-auto border-b border-line">
          {items.map((item) => {
            const selected = item.value === active;
            return (
              <button
                key={item.value}
                id={`${baseId}-tab-${item.value}`}
                role="tab"
                type="button"
                aria-selected={selected}
                aria-controls={`${baseId}-panel-${item.value}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActive(item.value)}
                onKeyDown={handleKeyDown}
                className={cn(
                  '-mb-px whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors duration-micro',
                  selected ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink',
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export function TabPanel({ value, children }: { value: string; children: ReactNode }) {
  const context = useContext(TabsContext);
  if (!context || context.active !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${context.baseId}-panel-${value}`}
      aria-labelledby={`${context.baseId}-tab-${value}`}
      tabIndex={0}
      className="pt-8 focus-visible:outline-none"
    >
      {children}
    </div>
  );
}

export function Stepper({ steps, className }: { steps: { title: string; description?: string }[]; className?: string }) {
  return (
    <ol className={cn('relative space-y-8 border-l border-line pl-8', className)}>
      {steps.map((step, index) => (
        <li key={step.title} className="relative">
          <span
            aria-hidden="true"
            className="absolute -left-[2.55rem] flex h-8 w-8 items-center justify-center rounded-full border border-line bg-surface font-mono text-xs font-bold text-primary"
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <p className="font-display text-base font-semibold text-ink">{step.title}</p>
          {step.description && <p className="mt-1.5 text-sm leading-relaxed text-muted">{step.description}</p>}
        </li>
      ))}
    </ol>
  );
}

/* ------------------------------------------------------------------ overlays */

/**
 * Modal dialog with a focus trap, `Esc` to dismiss, scroll lock and focus restoration. Built on
 * a plain element rather than `<dialog>` for consistent behaviour across the browsers we support.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    panelRef.current?.querySelector<HTMLElement>('button, a[href], input, select, textarea')?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-3xl' } as const;

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center p-0 sm:items-center sm:p-6">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-ink/55 backdrop-blur-sm"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative z-10 w-full animate-scale-in overflow-hidden rounded-t-xl bg-surface shadow-float sm:rounded-xl',
          sizes[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
          <h2 id={titleId} className="text-lg font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-1 rounded p-2 text-muted hover:bg-raised hover:text-ink focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-line bg-raised px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ navigation */

export function Breadcrumbs({ items, inverse }: { items: { label: string; path: string }[]; inverse?: boolean }) {
  if (items.length <= 1) return null;
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-2 text-sm">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={item.path} className="flex items-center gap-2">
              {isLast ? (
                <span aria-current="page" className={inverse ? 'font-medium text-white/90' : 'font-medium text-ink'}>
                  {item.label}
                </span>
              ) : (
                <Link to={item.path} className={cn('no-underline hover:underline', inverse ? 'text-white/70' : 'text-muted')}>
                  {item.label}
                </Link>
              )}
              {!isLast && (
                <span aria-hidden="true" className={inverse ? 'text-white/40' : 'text-line-strong'}>
                  /
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function Pagination({
  page,
  pages,
  onChange,
  className,
}: {
  page: number;
  pages: number;
  onChange: (page: number) => void;
  className?: string;
}) {
  if (pages <= 1) return null;

  // Keep the control compact on long lists: first, last, current and its neighbours.
  const windowed = new Set<number>([1, pages, page, page - 1, page + 1]);
  const visible = [...windowed].filter((value) => value >= 1 && value <= pages).sort((a, b) => a - b);

  return (
    <nav aria-label="Pagination" className={cn('flex items-center justify-center gap-1.5', className)}>
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className="rounded-md border border-line px-3 py-2 text-sm text-ink disabled:opacity-40 enabled:hover:border-primary enabled:hover:text-primary"
      >
        Previous
      </button>
      {visible.map((value, index) => {
        const previous = visible[index - 1];
        const gap = previous !== undefined && value - previous > 1;
        return (
          <span key={value} className="flex items-center gap-1.5">
            {gap && (
              <span aria-hidden="true" className="px-1 text-muted">
                …
              </span>
            )}
            <button
              type="button"
              onClick={() => onChange(value)}
              aria-current={value === page ? 'page' : undefined}
              aria-label={`Page ${value}`}
              className={cn(
                'min-w-10 rounded-md border px-3 py-2 text-sm font-medium',
                value === page
                  ? 'border-primary bg-primary text-primary-contrast'
                  : 'border-line text-ink hover:border-primary hover:text-primary',
              )}
            >
              {value}
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= pages}
        className="rounded-md border border-line px-3 py-2 text-sm text-ink disabled:opacity-40 enabled:hover:border-primary enabled:hover:text-primary"
      >
        Next
      </button>
    </nav>
  );
}

/* ------------------------------------------------------------------ data display */

export function Table({
  caption,
  head,
  children,
  className,
}: {
  caption?: string;
  head: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className={cn('w-full border-collapse text-left text-sm', className)}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="bg-raised text-xs uppercase tracking-wide text-muted">{head}</thead>
        <tbody className="divide-y divide-line">{children}</tbody>
      </table>
    </div>
  );
}

/**
 * Wraps a CMS-uploaded diagram. Diagrams are wide, so the frame provides horizontal scroll on
 * small screens, a caption, and a required alt text so the image is never unlabelled.
 */
export function DiagramFrame({
  src,
  alt,
  caption,
  className,
}: {
  src?: string;
  alt: string;
  caption?: string;
  className?: string;
}) {
  return (
    <figure className={cn('overflow-hidden rounded-lg border border-line bg-raised', className)}>
      <div className="overflow-x-auto p-4 sm:p-8">
        {src ? (
          <img src={src} alt={alt} loading="lazy" decoding="async" className="mx-auto min-w-[36rem] max-w-full sm:min-w-0" />
        ) : (
          <div className="hairline-grid flex min-h-56 items-center justify-center rounded-md border border-dashed border-line-strong p-8 text-center">
            <p className="max-w-sm text-sm text-muted">
              An architecture diagram has not been uploaded yet. Add one in the CMS to replace this placeholder.
            </p>
          </div>
        )}
      </div>
      {caption && (
        <figcaption className="border-t border-line px-4 py-3 text-sm text-muted sm:px-8">{caption}</figcaption>
      )}
    </figure>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cn('border-line', className)} />;
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>;
}

export type { HTMLAttributes };
